import {
  SwapVert as SwapVertIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { Button, IconButton, Divider } from '@mui/material';
import { Form, Formik, FormikHelpers } from 'formik';
import { find } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormikPanel, FormikRadioGroup, FormikSelectOption } from 'components';
import { TranslationNamespace } from 'i18n';
import { Sorting, SortOrder } from 'types';

type Props = {
  sorting: Sorting;
  initialValues: Sorting;
  sortingFields: FormikSelectOption[];
  onReset: () => void;
  onSubmit: (values: Sorting) => void;
};

export const SortingDropdown: React.FC<Props> = ({
  sorting,
  initialValues: initialValuesProp,
  sortingFields,
  onReset,
  onSubmit,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.sorting_dropdown',
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = useMemo(() => !!anchorEl, [anchorEl]);

  const initialValues = useMemo(
    () =>
      initialValuesProp || {
        field:
          find(sortingFields, (field) => field.value === 'createdAt')?.value ||
          '',
        sortOrder: SortOrder.Desc,
      },
    [initialValuesProp, sortingFields],
  );

  const sortOrderOptions = useMemo(
    () => [
      { value: SortOrder.Desc, label: t('sort_order.desc') },
      { value: SortOrder.Asc, label: t('sort_order.asc') },
    ],
    [t],
  );

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleReset = useCallback(() => {
    onReset();
    handleClose();
  }, [handleClose, onReset]);

  const handleSubmit = useCallback(
    (values: Sorting, helpers: FormikHelpers<Sorting>) => {
      helpers.setSubmitting(false);
      onSubmit(values);
      handleClose();
    },
    [handleClose, onSubmit],
  );

  const sortingDirty = useMemo(() => sorting?.field, [sorting]);

  return (
    <div>
      <IconButton onClick={handleClick}>
        <SwapVertIcon color={sortingDirty ? 'info' : 'inherit'} />
      </IconButton>
      <FormikPanel open={open} anchorEl={anchorEl} onClose={handleClose}>
        <div>
          <div className="tw-flex tw-items-center">
            <div className="tw-text-lg tw-font-bold tw-flex-1">
              {t('title')}
            </div>
            <CloseIcon className="tw-cursor-pointer" onClick={handleClose} />
          </div>
          <Formik
            initialValues={initialValues}
            enableReinitialize
            onSubmit={handleSubmit}
          >
            <Form>
              <div className="tw-grid tw-gap-2 tw-mt-4 tw-mb-8">
                <FormikRadioGroup
                  name="sortOrder"
                  options={sortOrderOptions}
                  formControlLabelSx={{ width: '100%' }}
                ></FormikRadioGroup>
                <Divider />
                <FormikRadioGroup
                  name="field"
                  options={sortingFields}
                  formControlLabelSx={{ width: '100%' }}
                ></FormikRadioGroup>
              </div>
              <div className="tw-grid tw-grid-cols-2 tw-gap-2">
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={handleReset}
                >
                  {t('buttons.reset')}
                </Button>
                <Button
                  variant="outlined"
                  color="info"
                  size="small"
                  type="submit"
                >
                  {t('buttons.submit')}
                </Button>
              </div>
            </Form>
          </Formik>
        </div>
      </FormikPanel>
    </div>
  );
};
